import { Link } from "react-router-dom";
import { Heart, ShoppingBag } from "lucide-react";
import { useShop } from "@/context/ShopContext";
import { eur } from "@/lib/api";
import { RARITY_STYLE, CATEGORY_LABEL } from "@/lib/constants";
import { StarRating } from "@/components/shop/StarRating";
import { ProductImage } from "@/components/shop/ProductImage";

export const ProductCard = ({ p, index = 0 }) => {
  const { addToCart, wishlist, toggleWishlist, cart } = useShop();
  const wished = wishlist.includes(p.id);
  const out = p.stock <= 0;
  const held = p.in_other_carts && !cart.some((i) => i.product_id === p.id);

  return (
    <div
      data-testid={`product-card-${p.id}`}
      className="group fade-up relative flex flex-col overflow-hidden rounded-2xl border border-black/[0.08] bg-[#FFFFFF] transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1 hover:border-amber-500/40 hover:shadow-[0_20px_60px_-20px_rgba(242,164,58,0.35)]"
      style={{ animationDelay: `${Math.min(index, 12) * 50}ms` }}
    >
      <Link to={`/prodotto/${p.id}`} className="relative block aspect-[4/5] overflow-hidden bg-[#F3E9D7]" data-testid={`product-link-${p.id}`}>
        <ProductImage src={p.image} alt={p.name} w={600} width={600} height={750} sizes="(min-width: 1280px) 25vw, (min-width: 768px) 33vw, 50vw" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-70" />
        <div className="absolute left-2 right-12 top-2 flex flex-wrap gap-1 sm:left-3 sm:top-3 sm:gap-1.5">
          <span className="max-w-full truncate rounded-md bg-black/70 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white backdrop-blur sm:px-2 sm:text-[10px]">{p.game}</span>
          {p.condition && <span className="rounded-md bg-emerald-500/90 px-2 py-0.5 text-[10px] font-bold text-black">{p.condition}</span>}
          {p.preorder && <span className="rounded-md bg-violet-500 px-2 py-0.5 text-[10px] font-bold text-white" data-testid={`preorder-badge-${p.id}`}>PREORDINE · {new Date(`${p.release_date}T00:00:00`).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" })}</span>}
        </div>
        {p.rarity && (
          <span className={`absolute bottom-3 left-3 rounded-md px-2 py-0.5 text-[10px] font-bold ${RARITY_STYLE[p.rarity] || "bg-slate-600"}`}>{p.rarity}</span>
        )}
        {out && <span className="absolute inset-0 flex items-center justify-center bg-white/70 font-display text-sm font-bold uppercase tracking-widest text-stone-800">{held ? <span className="px-3 text-center text-[11px] normal-case tracking-normal" data-testid={`product-held-${p.id}`}>Il prodotto è stato inserito nel carrello di un altro utente</span> : p.preorder ? "Preordini chiusi" : "Esaurito"}</span>}
      </Link>
      <button
        data-testid={`wishlist-toggle-${p.id}`}
        onClick={() => toggleWishlist(p.id)}
        className="absolute right-2 top-2 flex h-8 w-8 sm:right-3 sm:top-3 sm:h-9 sm:w-9 items-center justify-center rounded-full bg-black/50 backdrop-blur transition-colors hover:bg-black/80"
        aria-label="Lista desideri"
      >
        <Heart className={`h-4 w-4 ${wished ? "fill-rose-500 text-rose-500" : "text-white"}`} />
      </button>
      <div className="flex flex-1 flex-col gap-1.5 p-3 sm:gap-2 sm:p-4">
        <p className="text-[11px] uppercase tracking-wider text-stone-500">{CATEGORY_LABEL[p.category]}{p.set_name ? ` · ${p.set_name}` : ""}</p>
        <Link to={`/prodotto/${p.id}`} className="line-clamp-2 font-display text-sm font-bold sm:text-base leading-snug text-stone-900 hover:text-amber-600">{p.name}</Link>
        {p.rating_count > 0 && (
          <div className="flex items-center gap-1.5 text-xs text-stone-600">
            <StarRating value={p.rating_avg} size={12} />({p.rating_count})
          </div>
        )}
        {p.finish && <p className="text-xs text-stone-500">{p.finish}{p.condition ? ` · ${p.condition}` : ""}</p>}
        <div className="mt-auto flex items-center justify-between pt-2">
          <span className="flex flex-col">
            {p.market_price > p.price && <span className="font-mono text-xs text-stone-600 line-through" data-testid={`product-market-${p.id}`}>{eur(p.market_price)}</span>}
            <span className="font-mono text-base font-bold text-amber-600 sm:text-lg" data-testid={`product-price-${p.id}`}>{eur(p.price)}</span>
            {p.channel === "b2b" && <span className="text-[10px] font-semibold uppercase text-stone-500" data-testid={`product-vat-${p.id}`}>+ IVA</span>}
          </span>
          <button
            data-testid={`add-to-cart-${p.id}`}
            disabled={out}
            onClick={() => addToCart(p)}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-500 sm:h-10 sm:w-10 text-black transition-[transform,background-color] hover:scale-110 hover:bg-amber-400 disabled:cursor-not-allowed disabled:bg-stone-300 disabled:text-stone-500"
            aria-label="Aggiungi al carrello"
          >
            <ShoppingBag className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
